import React, { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { postRequestNew } from "../../utils/axiospostrequest";
import { load } from "@cashfreepayments/cashfree-js";
import "./PaymentModal.scss";
import MemberShipPlan from "../Dashboard/NewHomePage/MemberShipPlan";
import UserContext from "../../context/UserContext";

const PaymentModalStep = ({
  isOpen,
  onClose,
  initialStep = 1,
  onSecureSpot,
}) => {
  const { userDetail } = useContext(UserContext);
  const [step, setStep] = useState(initialStep);
  const [selectedSports, setSelectedSports] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [cashfree, setCashfree] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [amount] = useState(10);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 680);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const openModal = () => {
    setIsModalOpen(true);
    // onClose();
  };

  const closeModalNew = () => {
    setIsModalOpen(false);
    // onClose();
  };

  // Update isMobile state on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 680);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize); // Cleanup
  }, []);

  useEffect(() => {
    let isMounted = true;

    const initializeSDK = async () => {
      if (!isOpen) return;
      try {
        const sdk = await load({ mode: "production" });
        if (isMounted) {
          setCashfree(sdk);
        }
      } catch (error) {
        // console.error("Failed to load Cashfree SDK:", error);
        toast.error("Failed to initialize payment gateway.");
      }
    };

    initializeSDK();

    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  if (!isOpen && !paymentStatus) return null;

  const handlePayment = async () => {
    setIsLoading(true);
    const endPoint = `memberships/payments/create_an_order?amount=${amount}`;
    const token = localStorage.getItem("access_token");
    const data = {
      customer_details: {
        amount: amount,
      },
    };

    try {
      const res = await postRequestNew(endPoint, token, data);
      // console.log(res);
      const paymentSessionId = res.payment_session_id;
      setOrderId(res.order_id || `ORD-${Date.now()}`);

      if (!paymentSessionId) {
        console.error("Payment session ID is missing in the response.");
        toast.error("Failed to initiate payment. Please try again.");
        setIsLoading(false);
        return;
      }

      if (!cashfree) {
        toast.error("Payment gateway not initialized. Please try again.");
        setIsLoading(false);
        return;
      }

      const checkoutOptions = {
        paymentSessionId: paymentSessionId,
        redirectTarget: "_modal",
      };

      cashfree.checkout(checkoutOptions).then((result) => {
        setIsLoading(false);
        if (result.error) {
          console.error("Payment error:", result.error);
          setPaymentStatus("failed");
        } else if (result.redirect) {
          console.log("Payment will be redirected.");
        } else if (result.paymentDetails) {
          console.log(
            "Payment completed successfully:",
            result.paymentDetails.paymentMessage
          );
          setPaymentStatus("success");
        }
      });
    } catch (error) {
      console.error("Error during payment initiation:", error);
      toast.error("An error occurred. Please try again later.");
      setIsLoading(false);
    }
  };

  const sportsOptions = [
    "Basketball",
    "Football",
    "Pickleball",
    "Tennis",
    "Padel",
    "Cricket",
    "Badminton",
  ];

  const roleOptions = [
    "Business Owner",
    "Corporate Employee",
    "Freelancer",
    "Sports Professional",
    "Investor",
    "Student",
  ];

  const updateMemberInterestedSports = async (sports) => {
    try {
      const accessToken = window.localStorage.getItem("access_token");
      const url =
        "https://ballersapi.ballerslife.co/api/v1/memberships/member_interested_sports";
      const response = await fetch(url, {
        method: "POST",
        headers: {
          accept: "application/json",
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(sports),
      });

      if (!response.ok) {
        throw new Error("Failed to update member interested sports");
      }

      const data = await response.json();
      // console.log("API response:", data);
      toast.success("Sports preferences updated successfully!");
      return true;
    } catch (error) {
      console.error("Error updating member interested sports:", error);
      toast.error("Failed to update sports preferences.");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const updateMemberWorkProfile = async (workProfile) => {
    try {
      const accessToken = window.localStorage.getItem("access_token");
      const url =
        "https://ballersapi.ballerslife.co/api/v1/memberships/member_work_profile";
      const response = await fetch(
        `${url}?work_profile=${encodeURIComponent(workProfile)}`,
        {
          method: "POST",
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: "",
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update member work profile");
      }

      const data = await response.json();
      // console.log("API response:", data);
      toast.success("Work profile updated successfully!");
      return true;
    } catch (error) {
      console.error("Error updating member work profile:", error);
      toast.error("Failed to update work profile.");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSportToggle = (sport) => {
    let updatedSports;
    if (selectedSports.includes(sport)) {
      updatedSports = selectedSports.filter((s) => s !== sport);
    } else {
      updatedSports = [...selectedSports, sport];
    }
    setSelectedSports(updatedSports);
  };

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
  };

  const handleNext = async () => {
    if (step === 2) {
      if (selectedSports.length === 0) {
        toast.error("Please select at least one sport to continue.");
        return;
      }
      setIsLoading(true);
      const success = await updateMemberInterestedSports(selectedSports);
      if (!success) return;
    }

    if (step === 3) {
      if (!selectedRole) {
        toast.error("Please select a role to continue.");
        return;
      }
      setIsLoading(true);
      const success = await updateMemberWorkProfile(selectedRole);
      if (!success) return;
    }

    setStep((prev) => prev + 1);
  };

  const handleSecureSpot = () => {
    if (onSecureSpot) {
      onSecureSpot(); // Call the prop function to open BillDetailsModal and close PaymentModalStep
    }
  };
  const renderProgressCircles = () => {
    return (
      <div className="progress-circles">
        {[2, 3, 4].map((circleStep) => (
          <div key={circleStep} className="circle-wrapper">
            <div
              className={`circle ${step >= circleStep ? "active" : ""} ${
                step > circleStep ? "completed" : ""
              }`}
            >
              {step > circleStep &&
                (circleStep === 2 || circleStep === 3 || circleStep === 4) && (
                  <span className="paymentCheck">✔</span>
                )}
            </div>
            {circleStep < 4 && <div className="line"></div>}
          </div>
        ))}
      </div>
    );
  };

  const SuccessPopup = () => (
    <div className="payment-popup success">
      <button
        className="close-button"
        onClick={onClose}
        aria-label="Close popup"
      >
        ×
      </button>
      <div className="popup-icon success-icon">
        <span>✔</span>
      </div>
      <p className="payment_heading">You're officially a Baller!</p>
      <p className="popup-subtext">
        Game on! Your membership is now active, unlimited access, no extra fees,
        just pure play. Time to hit the court!
      </p>
      <p className="popup-subtext">
        Order ID: {orderId}
        <br />
        <br />
        Amount: Rs {amount}
      </p>
      <button className="modal-button" onClick={onClose}>
        Start Booking
      </button>
    </div>
  );

  const FailurePopup = () => (
    <div className="payment-popup failure">
      <button
        className="close-button"
        onClick={onClose}
        aria-label="Close popup"
      >
        ×
      </button>
      <div className="popup-icon failure-icon">
        <span>✖</span>
      </div>
      <p className="payment_heading">Oops, that didn't go through.</p>
      <p className="popup-subtext">
        Looks like something went wrong. No stress, try again or switch payment
        methods, we've got a spot waiting for you!
      </p>
      <p className="popup-subtext">
        Order ID: {orderId}
        <br />
        Amount: Rs {amount}
      </p>
      <button className="modal-button" onClick={() => handlePayment()}>
        Retry Payment
      </button>
    </div>
  );

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        {paymentStatus === "success" ? (
          <SuccessPopup />
        ) : paymentStatus === "failed" ? (
          <FailurePopup />
        ) : step === 1 ? (
          <>
            <h1 className="modal-heading">
              Welcome to Ballers! <br /> Your ultimate game plan starts right
              here!
            </h1>
            <p className="modal-subtext">
              Let's get to know you better so we can kick off your journey!
            </p>
            <p className="modal-subtext">Ready to level up?</p>
            <button className="modal-button" onClick={handleNext}>
              Let's go!
            </button>
          </>
        ) : step === 2 ? (
          <>
            {renderProgressCircles()}
            <h1 className="modal-heading">
              What's your sport of choice? <br /> Or are you a multi-sport
              Baller?
            </h1>
            <div className="options-list">
              {sportsOptions.map((sport) => (
                <label key={sport} className="option-item">
                  <input
                    type="checkbox"
                    checked={selectedSports.includes(sport)}
                    onChange={() => handleSportToggle(sport)}
                    disabled={isLoading}
                  />
                  <span>{sport}</span>
                </label>
              ))}
            </div>
            <button
              className="modal-button"
              onClick={handleNext}
              disabled={isLoading}
            >
              Let's keep this ball rolling!
            </button>
          </>
        ) : step === 3 ? (
          <>
            {renderProgressCircles()}
            <h1 className="modal-heading">
              What's your game off the field? <br /> Choose the role that best
              fits your hustle!
            </h1>
            <div className="options-list">
              {roleOptions.map((role) => (
                <label key={role} className="option-item">
                  <input
                    type="radio"
                    name="role"
                    checked={selectedRole === role}
                    onChange={() => handleRoleSelect(role)}
                    disabled={isLoading}
                  />
                  <span>{role}</span>
                </label>
              ))}
            </div>
            <button
              className="modal-button"
              onClick={handleNext}
              disabled={isLoading}
            >
              Time for the next move!
            </button>
          </>
        ) : step === 4 ? (
          <>
            {renderProgressCircles()}
            <h1 className="modal-heading">Show Us Your Game Face!</h1>
            <p className="modal-subtext">
              Upload a pic so we know who's bringing the heat to the courts.
            </p>
            <div className="upload-box">
              <input
                type="file"
                accept="image/*"
                id="file-upload"
                className="hidden-input"
              />
              <label htmlFor="file-upload">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#f5b115"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="upload-icon"
                >
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" x2="12" y1="3" y2="15" />
                </svg>
              </label>
            </div>
            <button
              className="modal-button"
              onClick={handleNext}
              disabled={isLoading}
            >
              Lock-in your game face
            </button>
            <button
              className="skip-btn"
              onClick={handleNext}
              disabled={isLoading}
            >
              Skip for Now
            </button>
          </>
        ) : step === 5 ? (
          <>
            {isMobile ? (
              // Mobile View (≤ 680px)
              <>
                {renderProgressCircles()}
                <h1 className="modal-heading">
                  Time to lock in your Ballers Membership!
                </h1>
                <p className="modal-subtext">
                  Here's everything you unlock when you join Ballers !
                </p>
                <ul className="membership-benefits">
                  <li>
                    <span className="checkmark"> </span>{" "}
                    <span>Unlimited Lifetime Access to 1000+ Facilities</span>
                  </li>
                  <li>
                    <span className="checkmark"> </span> Zero Booking Fees
                  </li>
                  <li>
                    <span className="checkmark"> </span> Members only Events &
                    Tournaments
                  </li>
                  <li>
                    <span className="checkmark"></span>Partner Perks & Discount
                  </li>
                  <li>
                    <span className="checkmark"> </span>
                    <p className="ownership-text">
                      Membership Packages start from{" "}
                      <span className="amountWrapper">
                        <span className="rupeeSymb"> ₹</span>
                        {/* <span className="strikethrough">3,00,000</span> */}
                        <span className="price-wrapper">
                          <span className="new-price">2000</span>
                          {/* <span className="launch-offer">(Launch offer)</span> */}
                        </span>
                      </span>
                    </p>
                  </li>
                </ul>
                <p className="modal-subtext warning">Only 500 spots per city</p>
                <button
                  className="modal-button"
                  onClick={() => openModal()}
                  disabled={isLoading}
                >
                  Membership Plans
                </button>
                <button className="skip-btn" onClick={onClose}>
                  No rush, you can always come back later!
                </button>
              </>
            ) : (
              <>
                {renderProgressCircles()}
                <h1 className="modal-heading">
                  Time to lock in your Ballers Membership!
                </h1>
                <p className="modal-subtext">
                  Here's EVERYTHING you unlock when you join the Ballers squad!
                </p>
                <ul className="membership-benefits">
                  <li>
                    <span className="checkmark"> </span>{" "}
                    <span>
                      Unlimited Lifetime Access - Play at 1000+ facilities
                      across the globe, anytime, anywhere.
                    </span>
                  </li>
                  <li>
                    <span className="checkmark"> </span> No Booking Fees - Your
                    membership covers it all, no hidden costs.
                  </li>
                  <li>
                    <span className="checkmark"> </span> Exclusive Member-Only
                    Events - Compete, connect, and party with the best of the
                    best.
                  </li>
                  <li>
                    <span className="checkmark"></span> VIP Tournament Access -
                    Get priority invites to major sports events and be in the
                    spotlight!
                  </li>
                  <li>
                    <span className="checkmark"> </span> Partner Perks &
                    Discounts - Get special deals on gear, coaching, nutrition,
                    and more!
                  </li>
                  <li>
                    <span className="checkmark"> </span>
                    <p className="ownership-text">
                      Membership packages start from{" "}
                      <span className="amountWrapper">
                        <span className="rupeeSymb"> ₹</span>
                        {/* <span className="strikethrough">3,00,000</span> */}
                        <span className="price-wrapper">
                          <span className="new-price">2,000</span>
                          {/* <span className="launch-offer">(Launch offer)</span> */}
                        </span>
                      </span>
                    </p>
                  </li>
                </ul>
                <p className="modal-subtext warning">
                  Still thinking? Spots are limited, and you don’t want to miss
                  out on this game-changing offer!
                </p>
                <button
                  className="modal-button"
                  onClick={() => openModal()}
                  disabled={isLoading}
                >
                  Membership Plans
                </button>
                <button className="skip-btn" onClick={onClose}>
                  No rush, you can always come back later!
                </button>
              </>
            )}
          </>
        ) : null}
      </div>
      {isModalOpen && (
        <MemberShipPlan onClose={closeModalNew} userDetail={userDetail} />
      )}
    </div>
  );
};

export default PaymentModalStep;
